import _ from 'lodash';
import center from '@turf/center';
import { points } from '@turf/helpers';
import area from '@turf/area';

import { createAction } from 'redux-actions';
import { createSelector } from 'reselect';

import SwaggerAPI from 'data/SwaggerAPI';
import { DashboardHelpers } from 'utils';

const SET_SELECTED_FIELD = 'fields/SET_SELECTED_FIELD';
const REQUEST_FIELDS = 'fields/REQUEST_FIELDS';
const RECEIVE_FIELDS = 'fields/RECEIVE_FIELDS';
const INSERT_FIELDS = 'fields/INSERT_FIELDS';
const UPDATE_FIELD = 'fields/UPDATE_FIELD';
const DELETE_FIELD = 'fields/DELETE_FIELD';
const FETCH_FIELD = 'fields/FETCH_FIELD';
const SET_GEOMETRY_LOAD = 'fields/SET_GEOMETRY_LOAD';
const DELETE_PROPERTY = 'properties/DELETE_PROPERTY';

const initialState = {
  isFetching: false,
  selectedField: undefined,
  activeFieldID: '',
  fields: [],
  geometryLoad: false,
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case REQUEST_FIELDS: {
      return { ...state, initialized: true, isFetchingProperties: true };
    }
    case RECEIVE_FIELDS: {
      if (!action.payload) return state;
      if (action.error) {
        return {
          ...state,
          error: action.payload,
        };
      }

      const newFields = _.unionWith(state.fields, action.payload, (field, newField) => field.id === newField.id);
      _.forEach(newFields, (f) => {
        f.area = (area(f.geometry) / 10000).toFixed(2);
        newFields[_.findIndex(newFields, { id: f.id })] = f;
      });

      return {
        ...state,
        fields: newFields,
        isFetchingProperties: false,
      };
    }
    case SET_GEOMETRY_LOAD: {
      return {
        ...state,
        geometryLoad: action.payload,
      };
    }
    case FETCH_FIELD: {
      if (!action.payload) return state;
      if (action.error) {
        return {
          ...state,
          error: action.payload,
        };
      }

      const newFields = [...state.fields];
      if (_.findIndex(newFields, { id: action.payload.id }) === -1) {
        newFields.push({ ...action.payload });
      }

      return {
        ...state,
        fields: newFields,
        isFetchingProperties: false,
      };
    }

    case INSERT_FIELDS: {
      const newFields = [...state.fields];
      const index = _.findIndex(newFields, { id: action.payload.id });
      if (index !== -1) {
        newFields[index] = { ...action.payload };
      } else {
        newFields.push({ ...action.payload });
      }

      return {
        ...state,
        fields: newFields,
      };
    }

    case UPDATE_FIELD: {
      const newFields = [...state.fields];
      const index = _.findIndex(newFields, { id: action.payload.id });
      if (index !== -1) {
        newFields[index] = { ...action.payload };
      }
      return {
        ...state,
        fields: newFields,
        selectedField: action.payload,
      };
    }

    case DELETE_FIELD: {
      const fieldsCopy = _.clone(state.fields);
      _.remove(fieldsCopy, (field) => field.id === state.activeFieldID);

      return {
        ...state,
        fields: fieldsCopy,
        selectedField: undefined,
        activeFieldID: undefined,
      };
    }

    case SET_SELECTED_FIELD: {
      if (action.fieldId === state.activeFieldID) return state;
      if (!action.fieldId) {
        return {
          ...state,
          selectedField: undefined,
          activeFieldID: undefined,
          selectedDevice: undefined,
        };
      }
      const field = _.find(state.fields, { id: action.fieldId });

      return {
        ...state,
        selectedField: field,
        activeFieldID: action.fieldId,
        selectedDevice: undefined,
      };
    }
    case DELETE_PROPERTY: {
      const fieldsCopy = _.clone(state.fields);
      const propertyID = action.payload;
      if (!action.error) {
        _.forEach(fieldsCopy, function (field) {
          if (field.property_id === propertyID) {
            _.set(field, 'property_id', '');
          }
        });
      }
      return {
        ...state,
        fields: fieldsCopy,
      };
    }

    default:
      return state;
  }
};

const Fields = (function () {
  const setSelectedField = (fieldId) => ({
    type: SET_SELECTED_FIELD,
    fieldId,
  });
  const setGeometryLoad = (payload) => ({
    type: SET_GEOMETRY_LOAD,
    payload,
  });

  const startFetchFields = () => ({ type: REQUEST_FIELDS });
  const fetchFields = createAction(RECEIVE_FIELDS, SwaggerAPI.fieldList);
  const fetchField = createAction(FETCH_FIELD, SwaggerAPI.fieldGet);
  const insertFields = createAction(INSERT_FIELDS, SwaggerAPI.fieldCreate);
  const updateField = (propertyId, payload) => ({ type: UPDATE_FIELD, propertyId, payload });
  const deleteField = createAction(DELETE_FIELD, SwaggerAPI.fieldDelete);
  const saveField = createAction(UPDATE_FIELD, SwaggerAPI.fieldUpdate);

  const isFetchingFields = (state) => state.fields.isFetching;
  const getActiveFieldId = (state) => state.fields.activeFieldID;
  const getActiveFieldObject = (state) => state.fields.selectedField;
  const getFields = (state) => state.fields.fields;
  const getProperties = (state) => state.properties.properties;
  const getDevices = (state) => state.devices.devices;
  const getActiveProperty = (state) => state.properties.selectedProperty;
  const getActiveOrganization = (state) => state.organizations.selectedOrganization;
  const getGeometryLoad = (state) => state.fields.geometryLoad;

  const getActiveFields = createSelector(
    [getFields, getProperties, getActiveProperty, getActiveOrganization],
    (fields, properties, selectedProperty, selectedOrganization) => {
      if (selectedProperty) {
        return _.filter(fields, { property_id: selectedProperty });
      } else if (selectedOrganization) {
        const orgProps = _.filter(properties, { organization_id: selectedOrganization.id });
        const orgPropIds = _.map(orgProps, 'id');
        return _.filter(fields, (field) => {
          return _.includes(orgPropIds, field.property_id);
        });
      } else {
        return fields;
      }
    }
  );

  const getActiveField = createSelector([getActiveFields, getActiveFieldId], (fields, selectedFieldId) => {
    return _.find(fields, { id: selectedFieldId });
  });

  const getFieldDevices = createSelector(
    [getActiveFields, getActiveField, getDevices],
    (fields, selectedField, devices) => {
      let selectedDevices = [];
      if (selectedField) {
        selectedDevices = _.filter(devices, { field_id: selectedField.id });
      } else {
        if (devices) {
          _.forEach(fields, (field) => {
            selectedDevices = _.filter(devices, { field_id: field.id });
          });
        }
      }
      return selectedDevices;
    }
  );

  const getFieldsBounds = createSelector([getActiveFields, getActiveField], (fields, selectedField) => {
    const latLngs = [
      [-90, 180],
      [90, -180],
    ];
    if (selectedField) {
      if (selectedField.geometry && selectedField.geometry && selectedField.geometry.coordinates) {
        _.forEach(selectedField.geometry.coordinates, (c) => {
          let coordArray;
          if (selectedField.geometry.type === 'MultiPolygon') {
            coordArray = c;
          } else {
            // Polygon has only one set of coords
            coordArray = [c];
          }
          _.forEach(coordArray, (coords) => {
            _.forEach(coords, (coord) => {
              if (coord[1] > latLngs[0][0]) {
                latLngs[0][0] = coord[1];
              }
              if (coord[1] < latLngs[1][0]) {
                latLngs[1][0] = coord[1];
              }
              if (coord[0] < latLngs[0][1]) {
                latLngs[0][1] = coord[0];
              }
              if (coord[0] > latLngs[1][1]) {
                latLngs[1][1] = coord[0];
              }
            });
          });
        });
      }
      return latLngs;
    }
    return DashboardHelpers.getBounds(fields);
  });

  const getFieldsCenter = createSelector([getFieldsBounds], (bounds) => {
    const features = points(bounds);
    const c = center(features);
    return c;
  });

  return {
    setSelectedField,
    setGeometryLoad,

    startFetchFields,
    fetchFields,
    fetchField,
    insertFields,
    updateField,
    deleteField,
    saveField,
    getActiveFieldId,
    getActiveFieldObject,

    getFields,
    getActiveFields,
    getActiveField,
    getFieldDevices,
    getFieldsBounds,
    getFieldsCenter,
    getGeometryLoad,
    isFetchingFields,
  };
})();

export default Fields;
